import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useSubscription } from '../../contexts/SubscriptionContext'
import { redirectToCustomerPortal } from '../../lib/stripe-client'
import { Settings, LogOut, ChevronDown, FileText, Shield, CreditCard } from 'lucide-react'

interface ProfileMenuProps {
  onNavigateToTerms?: () => void
  onNavigateToPrivacy?: () => void
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({ onNavigateToTerms, onNavigateToPrivacy }) => {
  const { user, session, signOut } = useAuth()
  const { subscription } = useSubscription()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    console.log('[ProfileMenu] Sign out clicked')

    // Prevent multiple clicks
    if (isSigningOut) {
      console.log('[ProfileMenu] Already signing out, ignoring click')
      return
    }

    setIsSigningOut(true)
    setIsOpen(false)

    try {
      console.log('[ProfileMenu] Calling signOut()...')
      const result = await signOut()
      console.log('[ProfileMenu] SignOut completed:', result)

      if (result?.error) {
        console.error('[ProfileMenu] Sign out error:', result.error)
        setIsSigningOut(false)
        alert(`Sign out failed: ${result.error.message}`)
        return
      }

      // Sign out successful - redirect to landing page
      console.log('[ProfileMenu] Sign out successful, redirecting to landing page...')
      navigate('/', { replace: true })

      // Defensive: Also use hard redirect as backup after short delay
      setTimeout(() => {
        if (window.location.pathname !== '/') {
          console.log('[ProfileMenu] Backup redirect executing...')
          window.location.href = '/'
        }
      }, 100)

    } catch (err) {
      console.error('[ProfileMenu] Sign out exception:', err)
      setIsSigningOut(false)
      alert(`Sign out failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  const handleManageSubscription = async () => {
    console.log('[ProfileMenu] Manage subscription clicked')
    setIsOpen(false)

    if (!session?.access_token) {
      console.error('[ProfileMenu] No access token available')
      alert('Unable to access subscription management. Please try signing in again.')
      return
    }

    try {
      console.log('[ProfileMenu] Redirecting to customer portal...')
      await redirectToCustomerPortal(session.access_token)
    } catch (err) {
      console.error('[ProfileMenu] Error redirecting to portal:', err)

      // Parse error response for better user messaging
      let errorMessage = 'Failed to open subscription management.'

      if (err instanceof Error) {
        const errorText = err.message.toLowerCase()

        if (errorText.includes('no stripe customer')) {
          errorMessage = 'Your account has complimentary access and does not require a subscription.'
        } else if (errorText.includes('no subscription')) {
          errorMessage = 'You do not have a subscription to manage.'
        } else if (errorText.includes('invalid')) {
          errorMessage = 'There was an issue with your subscription data. Please contact support.'
        } else if (errorText.includes('stripe')) {
          errorMessage = `Stripe error: ${err.message}`
        } else {
          errorMessage = `Error: ${err.message}`
        }
      }

      alert(errorMessage)
    }
  }

  if (!user) {
    return null
  }

  const userEmail = user.email || 'Unknown User'
  const displayName = user.user_metadata?.full_name || userEmail.split('@')[0]

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="
          flex items-center space-x-2 px-3 py-2
          bg-dark-secondary/50 hover:bg-dark-secondary/70
          border border-dark-tertiary/30 hover:border-dark-tertiary/50
          rounded-lg
          text-light-secondary hover:text-light-primary
          backdrop-blur-sm
          transition-all duration-200
          focus:outline-none
          focus:ring-2
          focus:ring-primary-500/50
        "
      >
        <div className="
          w-8 h-8 
          bg-gradient-to-br from-primary-600 to-primary-500
          rounded-full
          flex items-center justify-center
          text-white text-sm font-medium
        ">
          {displayName.charAt(0).toUpperCase()}
        </div>
        <div className="hidden sm:block text-left">
          <div className="text-sm font-medium text-light-primary truncate max-w-32">
            {displayName}
          </div>
          <div className="text-xs text-light-tertiary truncate max-w-32">
            {userEmail}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="
          absolute right-0 top-full mt-2 w-64
          bg-gray-900
          border border-gray-700
          rounded-lg
          shadow-xl
          z-50
          overflow-hidden
        ">
          {/* User info */}
          <div className="px-4 py-3 border-b border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="
                w-10 h-10 
                bg-gradient-to-br from-primary-600 to-primary-500
                rounded-full
                flex items-center justify-center
                text-white font-medium
              ">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-light-primary truncate">
                  {displayName}
                </div>
                <div className="text-xs text-light-tertiary truncate">
                  {userEmail}
                </div>
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-2">
            <button
              onClick={() => {
                setIsOpen(false)
                navigate('/account-settings')
              }}
              className="
                w-full px-4 py-2
                text-left text-sm text-light-secondary hover:text-light-primary
                hover:bg-gray-800
                transition-colors duration-200
                flex items-center space-x-3
              "
            >
              <Settings className="w-4 h-4" />
              <span>Account Settings</span>
            </button>

            {/* Manage Subscription - Only show for real Stripe subscriptions */}
            {subscription &&
             subscription.status === 'active' &&
             !['admin-access', 'lifetime-access'].includes(subscription.id) && (
              <button
                onClick={handleManageSubscription}
                className="
                  w-full px-4 py-2
                  text-left text-sm text-light-secondary hover:text-light-primary
                  hover:bg-gray-800
                  transition-colors duration-200
                  flex items-center space-x-3
                "
              >
                <CreditCard className="w-4 h-4" />
                <span>Manage Subscription</span>
              </button>
            )}

            <hr className="my-2 border-gray-700" />

            <button
              onClick={() => {
                setIsOpen(false)
                onNavigateToTerms?.()
              }}
              className="
                w-full px-4 py-2
                text-left text-sm text-light-secondary hover:text-light-primary
                hover:bg-gray-800
                transition-colors duration-200
                flex items-center space-x-3
              "
            >
              <FileText className="w-4 h-4" />
              <span>Terms of Service</span>
            </button>

            <button
              onClick={() => {
                setIsOpen(false)
                onNavigateToPrivacy?.()
              }}
              className="
                w-full px-4 py-2
                text-left text-sm text-light-secondary hover:text-light-primary
                hover:bg-gray-800
                transition-colors duration-200
                flex items-center space-x-3
              "
            >
              <Shield className="w-4 h-4" />
              <span>Privacy Policy</span>
            </button>

            <hr className="my-2 border-gray-700" />

            <button
              onClick={handleSignOut}
              disabled={isSigningOut}
              className="
                w-full px-4 py-2
                text-left text-sm text-red-400 hover:text-red-300
                hover:bg-red-500/10
                transition-colors duration-200
                flex items-center space-x-3
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              <LogOut className={`w-4 h-4 ${isSigningOut ? 'animate-spin' : ''}`} />
              <span>{isSigningOut ? 'Signing out...' : 'Sign Out'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}